// React imports
import React, { useState, useRef, useEffect, useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import axios from 'axios';
import prettyBytes from 'pretty-bytes';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faEnvelopeOpenText, faPaperclip, faPenField, faReply, faUndo, faDownload, faFileLines, faTable, faFileImage, faFile } from '@fortawesome/pro-light-svg-icons';
import { faArchive, faTrash, faXmark, faEnvelopeDot } from '@fortawesome/pro-regular-svg-icons';

// MUI imports
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import { red } from '@mui/material/colors';

// Local imports
import email from '../utils/email';
import Reply from './EmailPage.addreply.js';
import OpportunityEditor from './EmailPage.conversation.opportunity.js';
import NoteEditor from './EmailPage.noteeditor.js';
import readableId from '../../ReadableId.js';

function EmailConversation(props) {
    const { conversation, width, padding, organization, tokens, onRemove, accesstoken, notes, 
        setNotes, emailLoginInfo, exchangeRates, lockedExchangeRates, onMergeNewMessages,
        me, accounts, suppliers, seaPorts, unlocode, onUpdateOpportunity, setAccounts,
        opportunities, allOpportunities, read, onSetRead, onSetUnread, noControls, onClose, messageOrder = 'asc',
        refreshConversations,
    } = props;

    console.log('[EmailConversation] refreshConversations available?', typeof refreshConversations === 'function');

    const [ fullConversation, setFullConversation ] = useState(null);
    const [ error, setError ] = useState(null);
    const [ adding, setAdding ] = useState(null);
    const [ pendingMessage, setPendingMessage ] = useState(null);
    const [ refreshing, setRefreshing ] = useState(false);
    const [ originalOpportunity, setOriginalOpportunity ] = useState(null);
    const [ manuallyMarkedUnread, setManuallyMarkedUnread ] = useState(false);
    const addingRefTop = useRef(null);
    const addingRefBottom = useRef(null);
    const pendingMessageElRef = useRef(null);
    const prevConversation = useRef(null);
    const pendingMessageRef = useRef(null);
    pendingMessageRef.current = { pendingMessage, setPendingMessage };

    function _setAdding(_adding) {
        if (adding === _adding) return;
        setAdding(_adding);
        if (_adding && _adding !== 'opportunity') {
            scrollToAdding();
        }
    }

    function scrollToAdding() {
        // Scroll to the appropriate position based on message order
        const conversationElement = messageOrder === 'desc' ? addingRefTop.current : addingRefBottom.current;
        console.log('[EmailConversation] _setAdding - conversationElement', conversationElement);
        if (conversationElement) {
            setTimeout(() => {
                if (messageOrder === 'desc') {
                    // For newest first, scroll to top
                    conversationElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                    // For oldest first, scroll to bottom
                    conversationElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }
            }, 100);
        }
    }

    async function checkPendingMessageStatus() {
        if (!pendingMessageRef?.current?.pendingMessage) return;

        const pm = pendingMessageRef.current.pendingMessage;

        try {
            // Check the queue status
            const status = await email.getEmailStatus({
                org: organization,
                emailId: pm.queuedEmailId,
                monadaAccesstoken: accesstoken
            });

            if (status.status === 'sent') {
                // Email was sent, refresh the conversation to get the real message
                const _fullConversation = await email.getMessagesForConversation({ org: organization, tokens, conversationId: conversation[0].conversationId });
                setFullConversation(_fullConversation);
                pendingMessageRef.current.setPendingMessage(null);
            } else if (status.status === 'error') {
                // Update the pending message to show error
                pendingMessageRef.current.setPendingMessage(prev => ({
                    ...prev,
                    status: 'error',
                    error: status.error
                }));
            } else {
                // Still pending/processing, update status and check again
                pendingMessageRef.current.setPendingMessage(prev => ({
                    ...prev,
                    status: status.status
                }));
                setTimeout(checkPendingMessageStatus, 2000);
            }
        } catch (e) {
            console.error('[EmailConversation] Error checking pending message status:', e);
            setTimeout(checkPendingMessageStatus, 5000);
        }
    }

    useEffect(() => {
        (async () => {
            const _fullConversation = await email.getMessagesForConversation({ org: organization, tokens, conversationId: conversation[0].conversationId });
            setFullConversation(_fullConversation);
        })();
    }, [conversation[0].conversationId, organization, tokens]);

    useEffect(() => {
        if (!prevConversation.current || conversation[0].conversationId !== prevConversation.current) {
            prevConversation.current = conversation[0].conversationId;
            _setAdding(null);
            // Reset manually marked unread flag when switching conversations
            setManuallyMarkedUnread(false);
        }

        if (opportunities?.find(o => !o.status || o.status === 'draft' || o.status === 'pre-draft')) {
            _setAdding('opportunity');
        }
    }, [conversation[0].conversationId, opportunities])

    useEffect(() => {
        // Don't auto-mark as read if the user manually marked this conversation as unread
        if (manuallyMarkedUnread) return;
        
        if (_.some(fullConversation, c => c.unread && !read?.includes(c.id))) {
            email.markAsRead({ org: organization, tokens, messageIds: fullConversation.map(c => c.id) });
            if (onSetRead) onSetRead(fullConversation.map(c => c.id));
        }
    }, [fullConversation, manuallyMarkedUnread, read]);

    useEffect(() => {
        console.log('[EmailConversation] Refreshing', refreshing);
        if (refreshing) {
            console.log('[EmailConversation] Refreshing - request');
            email.getMessagesForConversation({ org: organization, tokens, conversationId: fullConversation[0].conversationId }).then((newMessages) => {
                console.log('[EmailConversation] Refreshing - Got new messages', newMessages);

                if (newMessages.length === fullConversation.length && refreshing < 3) {
                    onMergeNewMessages({ messages: newMessages });
                    setFullConversation(newMessages);
                    setTimeout(r => setRefreshing((r || 1) + 1), 1500);
                } else {
                    onMergeNewMessages({ messages: newMessages });
                    setFullConversation(newMessages);
                    setRefreshing(false);
                    // Scroll to top if newest first order after refresh
                    if (messageOrder === 'desc' && addingRefTop.current) {
                        setTimeout(() => {
                            addingRefTop.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                    }
                }
            });
        }
    }, [refreshing, messageOrder])

    if (!fullConversation || _.isEmpty(fullConversation)) return null;

    const messageIds = fullConversation.map(c => c.id);

    async function handleMarkAsUnread() {
        if (!fullConversation || !messageIds.length) return;
        try {
            // Mark as unread on the Outlook server
            await email.markAsUnread({ org: organization, tokens, messageIds });
            
            // Set flag to prevent auto-mark-as-read
            setManuallyMarkedUnread(true);
            
            // Update local conversation state to mark all messages as unread
            const updatedMessages = fullConversation.map(c => ({ ...c, unread: true }));
            setFullConversation(updatedMessages);
            
            // Update parent component's state
            if (onMergeNewMessages) {
                onMergeNewMessages({ messages: updatedMessages });
            }
            
            // Remove from read list so they appear as unread in the UI (bold, blue, etc.)
            if (onSetUnread) {
                onSetUnread(messageIds);
            }
        } catch (e) {
            console.error('Failed to mark as unread:', e);
            setError({ open: true, message: e?.message || 'Failed to mark conversation as unread' });
        }
    }

    function onArchive() {
        try {
            email.archive({ org: organization, tokens, conversationId: fullConversation[0].conversationId });
            onRemove({ message: 'Conversation archived' });
        } catch(e) {
            setError({ open: true, message: e.message });
        }
    }

    function onTrash() {
        try {
            email.trash({ org: organization, tokens, conversationId: fullConversation[0].conversationId });
            onRemove({ message: 'Conversation trashed' });
        } catch(e) {
            console.log('[gmail] trash error');
            console.error(e);
            setError({ open: true, message: e.message });
        }
    }

    function onNoteDone({ note }) {
        setNotes(prev => [ ...prev.filter(n => n.id !== note.id), note ]);
        _setAdding(null);
    }

    function onReply({ queuedEmailId, pendingMessage: pm } = {}) {
        console.log('[EmailConversation] onReply called', { queuedEmailId, pendingMessage: pm, refreshConversations: typeof refreshConversations });
        _setAdding(null);

        // If we have a pending message, show it in the conversation
        if (pm && queuedEmailId) {
            setPendingMessage({
                ...pm,
                queuedEmailId,
                senderEmail: emailLoginInfo?.email,
                senderName: emailLoginInfo?.name || 'Me',
                timestamp: Date.now()
            });
            // Start polling for status
            setTimeout(checkPendingMessageStatus, 1000);
            // Scroll to show the pending message placeholder
            setTimeout(() => {
                if (pendingMessageElRef.current) {
                    pendingMessageElRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        } else {
            setRefreshing(1);
        }

        // Refresh the conversation list to update sorting after sending a reply
        if (refreshConversations) {
            console.log('[EmailConversation] onReply - scheduling conversation list refresh');
            setTimeout(() => refreshConversations(true), 3000);
            setTimeout(() => refreshConversations(true), 8000);
        }
    }

    const globalMessageId = _(fullConversation || []).sortBy(a => dayjs(a.receivedDateTime).valueOf()).first()?.globalMessageId || fullConversation[0].conversationId;

    const events = [
        ...(fullConversation || []).map(c => ({ type: 'message', message: c, timestamp: dayjs(c.receivedDateTime).valueOf() })),
        ...(notes || []).map(n => ({ type: 'note', note: n, timestamp: n.created })),
        ...(_.compact((opportunities || []).map(o => {
            const sentEvents = o.log?.filter(l => l.action === 'sent-to-endcustomer');
            if (_.isEmpty(sentEvents)) return null;
            return sentEvents.map((e, i) => ({ type: 'opportunity', opportunity: o, timestamp: e.timestamp + 30 * 1000, last: i === sentEvents.length - 1 }));
        })).flat())
    ].sort((a, b) => {
        const diff = a.timestamp - b.timestamp;
        return messageOrder === 'desc' ? -diff : diff;
    });

    if (refreshing) {
        return (
            <Box sx={{ width: width || '80vw', p: _.isUndefined(padding) ? 3 : padding, pr: _.isUndefined(padding) ? 6 : padding }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Tooltip title='Mark as unread'>
                            <IconButton onClick={handleMarkAsUnread}>
                                <FontAwesomeIcon icon={faEnvelopeDot} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Archive conversation'>
                            <IconButton onClick={onArchive}>
                                <FontAwesomeIcon icon={faArchive} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Trash conversation'>
                            <IconButton onClick={onTrash}>
                                <FontAwesomeIcon icon={faTrash} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Tooltip title='Close conversation'>
                        <IconButton onClick={() => onClose()}>
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ mb: 3, pb: 1.5 }}>
                    <Typography 
                        variant='h6' 
                        sx={{ 
                            fontSize: '14px', 
                            fontWeight: 600,
                            color: '#0f172a',
                            fontFamily: 'Poppins, sans-serif'
                        }}
                    >
                        Subject: {_.last(fullConversation)?.subject || <i>No subject</i>}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            </Box>
        )
    }

    return (
        <Box sx={{ width: width || '80vw', p: _.isUndefined(padding) ? 3 : padding, pr: _.isUndefined(padding) ? 6 : padding }}>
            {!noControls && (
                <Paper sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 2, p: 1 }}>
                    <ActionBar setAdding={_setAdding} setOriginalOpportunity={setOriginalOpportunity} />
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title='Mark as unread'>
                        <IconButton color='primary' onClick={handleMarkAsUnread}>
                            <FontAwesomeIcon icon={faEnvelopeDot} />
                        </IconButton>
                    </Tooltip>
                    <Divider orientation='vertical' flexItem />
                    <Tooltip title='Archive conversation'>
                        <IconButton color='primary' onClick={onArchive}>
                            <FontAwesomeIcon icon={faArchive} />
                        </IconButton>
                    </Tooltip>
                    <Divider orientation='vertical' flexItem />
                    <Tooltip title='Trash conversation'>
                        <IconButton color='primary' onClick={onTrash}>
                            <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                    </Tooltip>
                    <Divider orientation='vertical' flexItem />
                    <Tooltip title='Close conversation'>
                        <IconButton color='primary' onClick={() => onClose()}>
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                    </Tooltip>
                </Paper>
            )}
            <Box sx={{ mb: 3, pb: 1.5 }}>
                <Typography 
                    variant='h6' 
                    sx={{ 
                        fontSize: '14px', 
                        fontWeight: 600,
                        color: '#0f172a',
                        fontFamily: 'Poppins, sans-serif'
                    }}
                >
                    Subject: {_.last(fullConversation)?.subject || <i>No subject</i>}
                </Typography>
            </Box>
            {messageOrder === 'desc' && (
                <>
                    {pendingMessage && (
                        <div ref={pendingMessageElRef}>
                            <PendingMessage
                                message={pendingMessage}
                                myEmail={emailLoginInfo?.email}
                                organization={organization}
                            />
                        </div>
                    )}
                    <div ref={addingRefTop}>
                        {adding === 'note' && (
                            <NoteEditor 
                                organization={organization}
                                accesstoken={accesstoken}
                                conversationId={globalMessageId}
                                onNoteDone={onNoteDone}
                                onClose={() => _setAdding(null)}
                            />
                        )}
                        {adding === 'reply' && (
                            <Reply 
                                conversation={fullConversation} 
                                onReply={onReply} 
                                onClose={() => _setAdding(null)} 
                                myEmail={emailLoginInfo.email} 
                                myName={emailLoginInfo.name}
                                users={organization.users}
                                organization={organization}
                                tokens={tokens}
                                accesstoken={accesstoken}
                                accounts={accounts}
                                refreshConversations={refreshConversations}
                            />
                        )}
                        {adding === 'opportunity' && (
                            <OpportunityEditor
                                onClose={({ sent = false, id, queuedEmailId, pendingMessage: pm } = {}) => {
                                    if (sent) {
                                        setOriginalOpportunity(null);
                                    }
                                    if (!sent) {
                                        const opportunity = opportunities?.find(o => !o.status || o.status === 'draft' || o.status === 'pre-draft');
                                        if (opportunity) {
                                            onUpdateOpportunity({ opportunityId: opportunity.id, func: (op) => originalOpportunity });
                                            setOriginalOpportunity(null);
                                        }
                                    }
                                    _setAdding(null);
                                    if (pm && queuedEmailId) {
                                        setPendingMessage({
                                            ...pm,
                                            queuedEmailId,
                                            senderEmail: emailLoginInfo?.email,
                                            senderName: emailLoginInfo?.name || 'Me',
                                            timestamp: Date.now()
                                        });
                                        setTimeout(checkPendingMessageStatus, 1000);
                                        setTimeout(() => {
                                            if (pendingMessageElRef.current) {
                                                pendingMessageElRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }
                                        }, 100);
                                        if (refreshConversations) {
                                            setTimeout(() => refreshConversations(true), 3000);
                                            setTimeout(() => refreshConversations(true), 8000);
                                        }
                                    }
                                }}
                                organization={organization}
                                accesstoken={accesstoken}
                                conversation={fullConversation}
                                conversationId={fullConversation[0].conversationId}
                                globalMessageId={globalMessageId}
                                exchangeRates={exchangeRates}
                                lockedExchangeRates={lockedExchangeRates}
                                me={me}
                                emailLoginInfo={emailLoginInfo}
                                accounts={accounts}
                                suppliers={suppliers}
                                seaPorts={seaPorts}
                                unlocode={unlocode}
                                tokens={tokens}
                                onUpdateOpportunity={onUpdateOpportunity}
                                setAccounts={setAccounts}
                                opportunity={opportunities?.find(o => !o.status || o.status === 'draft' || o.status === 'pre-draft')}
                                opportunities={opportunities}
                                allOpportunities={allOpportunities}
                                scrollToAdding={scrollToAdding}
                            />
                        )}
                    </div>
                </>
            )}
            {events.map((event, eventIndex) => {
                if (event.type === 'message') {
                    const isLastMessage = eventIndex === events.length - 1 || events.slice(eventIndex + 1).find(e => e.type === 'message') === undefined;
                    return <Conversation 
                        key={event.message.id} 
                        message={event.message} 
                        myEmail={emailLoginInfo?.email} 
                        organization={organization} 
                        accesstoken={accesstoken} 
                        tokens={tokens}
                        isLastMessage={isLastMessage}
                    />
                } else if (event.type === 'note') {
                    return (
                        <Note 
                            key={event.note.id} 
                            note={event.note} 
                            organization={organization} 
                            onUpdate={onNoteDone} 
                            conversationId={globalMessageId} 
                            accesstoken={accesstoken} 
                        />
                    )
                } else if (event.type === 'opportunity') {
                    return (
                        <Opportunity 
                            key={`${event.opportunity.id}-${event.timestamp}`} 
                            opportunity={event.opportunity} 
                            organization={organization} 
                            last={event.last} 
                            onUpdateOpportunity={onUpdateOpportunity} 
                            accesstoken={accesstoken} 
                            disabled={!!adding}
                            timestamp={event.timestamp}
                            setOriginalOpportunity={setOriginalOpportunity}
                        />
                    )
                }
            })}
            {messageOrder !== 'desc' && (
                <div ref={addingRefBottom}>
                    {adding === 'note' && (
                        <NoteEditor 
                            organization={organization}
                            accesstoken={accesstoken}
                            conversationId={globalMessageId}
                            onNoteDone={onNoteDone}
                            onClose={() => _setAdding(null)}
                        />
                    )}
                    {adding === 'reply' && (
                        <Reply 
                            conversation={fullConversation} 
                            onReply={onReply} 
                            onClose={() => _setAdding(null)} 
                            myEmail={emailLoginInfo.email} 
                            myName={emailLoginInfo.name}
                            users={organization.users}
                            organization={organization}
                            tokens={tokens}
                            accesstoken={accesstoken}
                            accounts={accounts}
                        />
                    )}
                    {adding === 'opportunity' && (
                        <OpportunityEditor
                            onClose={({ sent = false, id, queuedEmailId } = {}) => {
                                if (sent) {
                                    setOriginalOpportunity(null);
                                }
                                if (!sent) {
                                    const opportunity = opportunities?.find(o => !o.status || o.status === 'draft' || o.status === 'pre-draft');
                                    if (opportunity) {
                                        onUpdateOpportunity({ opportunityId: opportunity.id, func: (op) => originalOpportunity });
                                        setOriginalOpportunity(null);
                                    }
                                }
                                _setAdding(null);
                            }}
                            organization={organization}
                            accesstoken={accesstoken}
                            conversation={fullConversation}
                            conversationId={fullConversation[0].conversationId}
                            globalMessageId={globalMessageId}
                            exchangeRates={exchangeRates}
                            lockedExchangeRates={lockedExchangeRates}
                            me={me}
                            emailLoginInfo={emailLoginInfo}
                            accounts={accounts}
                            suppliers={suppliers}
                            seaPorts={seaPorts}
                            unlocode={unlocode}
                            tokens={tokens}
                            onUpdateOpportunity={onUpdateOpportunity}
                            setAccounts={setAccounts}
                            opportunity={opportunities?.find(o => !o.status || o.status === 'draft' || o.status === 'pre-draft')}
                            opportunities={opportunities}
                            allOpportunities={allOpportunities}
                            scrollToAdding={scrollToAdding}
                        />
                    )}
                    {pendingMessage && (
                        <div ref={pendingMessageElRef}>
                            <PendingMessage
                                message={pendingMessage}
                                myEmail={emailLoginInfo?.email}
                                organization={organization}
                            />
                        </div>
                    )}
                    {!adding && !noControls && !pendingMessage && (
                        <Box sx={{ mt: 3, pb: 3, width: '100%' }}>
                            <ActionBar setAdding={_setAdding} setOriginalOpportunity={setOriginalOpportunity} />
                        </Box>
                    )}
                </div>
            )}
            {messageOrder === 'desc' && (
                <>
                    {!adding && !noControls && !pendingMessage && (
                        <Box sx={{ mt: 3, pb: 3, width: '100%' }}>
                            <ActionBar setAdding={_setAdding} setOriginalOpportunity={setOriginalOpportunity} />
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}

function ActionBar(props) {
    const { setAdding, setOriginalOpportunity } = props;
    return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
            <Button
                variant='text'
                color='primary'
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textTransform: 'none', gap: 1 }}
                onClick={() => setAdding('reply')}
            >
                <FontAwesomeIcon icon={faReply} />
                <Typography variant='caption'>Reply</Typography>
            </Button>
            <div style={{ flex: 0, borderRight: '1px solid #eee' }} />
            <Button
                variant='text'
                color='primary'
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textTransform: 'none', gap: 1 }}
                onClick={() => {
                    setOriginalOpportunity(null);
                    setAdding('opportunity');
                }}

            >
                <FontAwesomeIcon icon={faEnvelopeOpenText} />
                <Typography variant='caption'>Quote</Typography>
            </Button>
        </Box>
    );
}

export default EmailConversation;

function Opportunity(props) {
    const { opportunity, organization, last, disabled, onUpdateOpportunity, accesstoken, timestamp, setOriginalOpportunity } = props;
    const [ revising, setRevising ] = useState(false);
    const [ revisingError, setRevisingError ] = useState(null);

    async function onRevise() {
        setRevising(true);
        try {
            setOriginalOpportunity(_.cloneDeep(opportunity));
            onUpdateOpportunity({ opportunity: { ...opportunity, status: null } });
            setRevising(false);
        } catch(e) {
            setRevising(false);
            setRevisingError(e.message);
            await new Promise(resolve => setTimeout(resolve, 3000));
            setRevisingError(null);
        }
    }
    
    if (last) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant='caption' color='primary' component={Box}>Opportunity #{readableId(opportunity)} sent to customer on {dayjs(timestamp).format('DD/MM/YYYY HH:mm')}.</Typography>
                <IconButton color='primary' onClick={() => {
                    window.open(`/organization/${organization.alias}/opportunities/${opportunity.id}`, '_blank');
                }}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </IconButton>
                <Button 
                    variant='outlined' 
                    color={revisingError ? 'error' : 'primary'} 
                    disabled={disabled || revising} 
                    sx={{ textTransform: 'none' }}
                    onClick={revisingError ? null : onRevise}
                >
                    {revising ? 
                        <CircularProgress size={16} sx={{ color: !revising ? 'primary.main' : 'inherit' }} /> : revisingError ?
                        <Tooltip color='error' title={revisingError} open={true}>
                            <FontAwesomeIcon icon={faXmark} style={{ color: red[500] }} />
                        </Tooltip> :
                        <FontAwesomeIcon icon={faPenField} />}&nbsp;&nbsp;Revise offer
                </Button>
            </Box>
        )
    } else {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='caption' color='secondary' component={Box}>Opportunity {readableId(opportunity)} sent to customer on {dayjs(timestamp).format('DD/MM/YYYY HH:mm')} (not latest offer).</Typography>
                <IconButton color='secondary' onClick={() => {
                    window.open(`/organization/${organization.alias}/opportunities/${opportunity.id}`, '_blank');
                }}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </IconButton>
            </Box>
        )
    }
}

// Helper function to get file type styling
function getAttachmentStyle(attachment) {
    const fileName = attachment.name || '';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const contentType = attachment.contentType?.toLowerCase() || '';
    
    // Determine file type
    const isPDF = extension === 'pdf' || contentType.includes('pdf');
    const isExcel = ['xls', 'xlsx', 'csv'].includes(extension) || contentType.includes('spreadsheet') || contentType.includes('excel');
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension) || contentType.startsWith('image/');
    const isZip = ['zip', 'rar', '7z', 'tar', 'gz'].includes(extension) || contentType.includes('zip') || contentType.includes('archive');
    
    if (isPDF) {
        return {
            icon: faFileLines,
            bgColor: '#FEF2F2',
            iconColor: '#EF4444',
            theme: 'pdf'
        };
    } else if (isExcel) {
        return {
            icon: faTable,
            bgColor: '#ECFDF5',
            iconColor: '#10B981',
            theme: 'excel'
        };
    } else if (isImage) {
        return {
            icon: faFileImage,
            bgColor: '#F5F3FF',
            iconColor: '#9333EA',
            theme: 'image'
        };
    } else {
        return {
            icon: faFile,
            bgColor: '#F8FAFC',
            iconColor: '#64748B',
            theme: 'general'
        };
    }
}

// Helper function to format file size
function formatFileSize(size) {
    if (!size) return '';
    try {
        return prettyBytes(size);
    } catch (e) {
        // Fallback for very large numbers
        if (size >= 1024 * 1024 * 1024) {
            return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
        } else if (size >= 1024 * 1024) {
            return `${(size / (1024 * 1024)).toFixed(2)} MB`;
        } else if (size >= 1024) {
            return `${(size / 1024).toFixed(2)} KB`;
        }
        return `${size} B`;
    }
}

function Conversation(props) {
    const { message, myEmail, organization, accesstoken, tokens, isLastMessage } = props;
    const [ downloadingImages, setDownloadingImages ] = useState(false);
    const [ inlineImages, setInlineImages ] = useState([]);
    const [ loadingAttachment, setLoadingAttachment ] = useState([]);

    async function onDisplayInlineImages() {
        setDownloadingImages(true);

        const urls = [];
        const attachments = message.attachments || [];

        console.log('[EmailPage] attachments', attachments);
        if (attachments && attachments.length > 0) {
            for (let i = 0; i < attachments.length; i++) {
                const attachment = attachments[i];
                console.log('[EmailPage] message', message);
                try {
                    const url = await email.getAttachmentUrl({ org: organization, monadaAccesstoken: accesstoken, tokens, messageId: message.id, attachment: attachment });
                    urls.push({ url, name: attachment.name, id: attachment.id, inlineId: attachment.inlineId });
                } catch (err) {
                    console.error('[EmailPage] Error getting attachment URL', err);
                }
            }
        }
        console.log('[EmailPage] urls', urls);

        setDownloadingImages(false);

        setInlineImages(i => [ ...i, ...urls ]);
    }


    const html = useMemo(() => {
        let html = message.body.html || '';
        // Replace all cid: references with actual image URLs
        for (const image of inlineImages) {
            if (image.inlineId) {
                // Replace cid: references in various formats
                const inlineId = image.inlineId.replace(/[<>]/g, '');
                html = html.replace(new RegExp(`cid:${inlineId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), image.url);
                // Also replace in img src attributes
                html = html.replace(new RegExp(`src=["']cid:${inlineId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']`, 'gi'), `src="${image.url}"`);
            }
        }
        return html;
    }, [message.body.html, inlineImages]);

    console.log('[EmailPage] html', html.length);

    const senderName = message.sender?.emailAddress?.address === myEmail 
        ? 'Me' 
        : (message.sender?.emailAddress?.name || message.sender?.emailAddress?.address);
    const senderEmail = message.sender?.emailAddress?.address;
    
    // Format all TO recipients with names and email addresses
    const formatRecipient = (recipient) => {
        if (!recipient?.emailAddress) return '';
        const name = recipient.emailAddress.address === myEmail
            ? 'Me'
            : (recipient.emailAddress.name || recipient.emailAddress.address);
        const email = recipient.emailAddress.address;
        if (email === myEmail) {
            return 'Me';
        }
        return name && name !== email ? `${name} <${email}>` : email;
    };
    
    const allToRecipients = (message.toRecipients || []).map(formatRecipient).filter(Boolean);

    return (
        <Box 
            key={message.id} 
            sx={{ 
                mb: 2,
                display: 'flex',
                position: 'relative',
                pl: 4
            }}
        >
            {/* Thread line connecting avatars */}
            {!isLastMessage && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '19px',
                        top: '40px',
                        bottom: '-16px',
                        width: '2px',
                        backgroundColor: '#e2e8f0',
                        zIndex: 0
                    }}
                />
            )}
            
            {/* Avatar */}
            <Box sx={{ mr: 2, position: 'relative', zIndex: 1 }}>
                <Avatar 
                    {...stringAvatar(senderName)}
                    sx={{
                        width: 36,
                        height: 36,
                        fontSize: '13px',
                        ...stringAvatar(senderName).sx
                    }}
                />
            </Box>
            
            {/* Message Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #f1f5f9',
                        borderRadius: '8px',
                        padding: '16px',
                        fontFamily: 'Poppins, sans-serif'
                    }}
                >
                    {/* Sender Line 1: Name (Bold) and Timestamp (Right-aligned) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography 
                            variant='body1' 
                            sx={{ 
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#0f172a',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                        >
                            {senderName}
                            {senderEmail && senderEmail !== myEmail && (
                                <span> &lt;{senderEmail}&gt;</span>
                            )}
                            </Typography>
                        <Typography 
                            variant='caption' 
                            sx={{ 
                                fontSize: '12px',
                                color: '#64748b',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                        >
                                    {dayjs(message.receivedDateTime).format('DD/MM/YYYY HH:mm')}
                                </Typography>
                            </Box>
                    
                    {/* Sender Line 2: Recipients with details dropdown */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography 
                            variant='caption' 
                            sx={{ 
                                fontSize: '12px',
                                color: '#64748b',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                        >
                            to: {allToRecipients.length > 0 ? allToRecipients.join(', ') : 'N/A'}
                        {!_.isEmpty(message.ccRecipients) && (
                                <span>, Cc: {message.ccRecipients.map(r => {
                                    const name = r.emailAddress?.name;
                                    const email = r.emailAddress?.address;
                                    return name && name !== email ? `${name} <${email}>` : (email || '');
                                }).filter(Boolean).join(', ')}</span>
                            )}
                        </Typography>
                    </Box>
                    
                {message.hasAttachments && message.body.html.includes('cid:') && _.isEmpty(inlineImages) && (
                        <Alert severity='info' sx={{ mt: 2, mb: 2 }} action={
                        <Button variant='text' color='primary' onClick={onDisplayInlineImages} disabled={downloadingImages}>
                            {downloadingImages ? <CircularProgress size={16} sx={{ color: 'primary.main' }} /> : 'Display'}
                        </Button>
                    }>
                        Email contains inline images. Should we display them?
                    </Alert>
                )}
                    
                    {/* Email Body */}
                    <Box 
                        sx={{ 
                            mt: 2,
                            maxWidth: '100%',
                            overflowX: 'auto',
                            overflowY: 'hidden'
                        }}
                    >
                        <EmailIframe html={html} />
                    </Box>
                    
                    {message.attachments && message.attachments.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                            {message.attachments.map(attachment => {
                            if (attachment.isInline) {
                                return null;
                            }
                                const style = getAttachmentStyle(attachment);
                                const fileName = attachment.name || 'Attachment';
                                const fileSize = formatFileSize(attachment.size);
                                
                            return (
                                    <Box
                                        key={attachment.id}
                                        sx={{
                                            backgroundColor: '#F8FAFC',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            width: '100%',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: '#F1F5F9',
                                            }
                                        }}
                                    >
                                        {/* File Icon */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <FontAwesomeIcon 
                                                icon={style.icon} 
                                                style={{ 
                                                    fontSize: '32px', 
                                                    color: style.iconColor 
                                                }} 
                                            />
                                        </Box>
                                        
                                        {/* File info */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    fontFamily: 'Poppins, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    color: '#0F172A',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    mb: 0.5
                                                }}
                                            >
                                                {fileName}
                                    </Typography>
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    fontFamily: 'Poppins, sans-serif',
                                                    fontSize: '12px',
                                                    color: '#64748B',
                                                }}
                                            >
                                                {fileSize}
                                            </Typography>
                                        </Box>
                                        
                                        {/* Download button */}
                                    <IconButton
                                            size='small'
                                        disabled={loadingAttachment.includes(attachment.id)}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                        setLoadingAttachment(la => [...la, attachment.id]);
                                                try {
                                        const url = await email.getAttachmentUrl({ org: organization, monadaAccesstoken: accesstoken, tokens, messageId: message.id, attachment: attachment });
                                        setLoadingAttachment(la => la.filter(a => a !== attachment.id));
                                        window.open(url, '_blank');
                                                } catch (err) {
                                                    console.error('[EmailPage] Error downloading attachment', err);
                                                    setLoadingAttachment(la => la.filter(a => a !== attachment.id));
                                                    alert(err.message || 'Failed to download attachment. Please reconnect your email.');
                                                }
                                            }}
                                            sx={{
                                                color: '#4F46E5',
                                                '&:hover': {
                                                    backgroundColor: '#EEF2FF',
                                                    color: '#4338CA'
                                                },
                                                '&:disabled': {
                                                    color: '#94A3B8'
                                                }
                                            }}
                                        >
                                            {loadingAttachment.includes(attachment.id) ? (
                                                <CircularProgress size={16} sx={{ color: '#4F46E5' }} />
                                            ) : (
                                                <FontAwesomeIcon icon={faDownload} style={{ fontSize: '16px' }} />
                                            )}
                                    </IconButton>
                                </Box>
                            )
                        })}
                    </Box>
                )}
            </Box>
            </Box>
        </Box>
    );
}

function PendingMessage({ message, myEmail, organization }) {
    const senderName = 'Me';
    const senderEmail = message.senderEmail;

    const formatRecipient = (recipient) => {
        if (!recipient) return '';
        const name = recipient.name;
        const email = recipient.address || recipient;
        return name && name !== email ? `${name} <${email}>` : email;
    };

    const allToRecipients = (message.toRecipients || []).map(formatRecipient).filter(Boolean);

    const getStatusText = () => {
        switch (message.status) {
            case 'pending':
                return 'Queued...';
            case 'processing':
                return 'Sending...';
            case 'error':
                return `Failed: ${message.error || 'Unknown error'}`;
            default:
                return 'Sending...';
        }
    };

    const getStatusColor = () => {
        switch (message.status) {
            case 'error':
                return '#ef4444';
            default:
                return '#64748b';
        }
    };

    return (
        <Box
            sx={{
                mb: 2,
                display: 'flex',
                position: 'relative',
                pl: 4,
                opacity: message.status === 'error' ? 0.7 : 0.85
            }}
        >
            {/* Avatar */}
            <Box sx={{ mr: 2, position: 'relative', zIndex: 1 }}>
                <Avatar
                    {...stringAvatar(senderName)}
                    sx={{
                        width: 36,
                        height: 36,
                        fontSize: '13px',
                        ...stringAvatar(senderName).sx
                    }}
                />
            </Box>

            {/* Message Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        backgroundColor: '#f8fafc',
                        border: '1px dashed #cbd5e1',
                        borderRadius: '8px',
                        padding: '16px',
                        fontFamily: 'Poppins, sans-serif'
                    }}
                >
                    {/* Header with status */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography
                            variant='body1'
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#0f172a',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                        >
                            {senderName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {message.status !== 'error' && (
                                <CircularProgress size={12} sx={{ color: '#64748b' }} />
                            )}
                            <Typography
                                variant='caption'
                                sx={{
                                    fontSize: '11px',
                                    color: getStatusColor(),
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 500
                                }}
                            >
                                {getStatusText()}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Recipients */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography
                            variant='caption'
                            sx={{
                                fontSize: '12px',
                                color: '#64748b',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                        >
                            to: {allToRecipients.length > 0 ? allToRecipients.join(', ') : 'N/A'}
                            {!_.isEmpty(message.ccRecipients) && (
                                <span>, Cc: {message.ccRecipients.map(formatRecipient).filter(Boolean).join(', ')}</span>
                            )}
                        </Typography>
                    </Box>

                    {/* Email Body */}
                    <Box sx={{ mt: 2, maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                        <EmailIframe html={message.body} />
                    </Box>

                    {/* Sending indicator */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid #e2e8f0'
                    }}>
                        {message.status !== 'error' && (
                            <CircularProgress size={16} sx={{ color: '#94a3b8' }} />
                        )}
                        <Typography sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: message.status === 'error' ? '#ef4444' : '#94a3b8',
                            fontFamily: 'Poppins, sans-serif',
                            fontStyle: 'italic'
                        }}>
                            {getStatusText()}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function Note(props) {
    const { note, organization, accesstoken, conversationId, onUpdate } = props;
    const [openDialog, setOpenDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deletingFailed, setDeletingFailed] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [restoringFailed, setRestoringFailed] = useState(false);
    
    const html = note.html;
    // Add bold and underline styling to email mentions
    const styledHtml = html.replace(/@([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<strong><u>@$1</u></strong>');

    console.log('[Note] note', note);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const { data } = await axios.put(`/api/emailconversations/${encodeURIComponent(conversationId)}/note/${note.id}?organizationId=${organization.id}&accesstoken=${accesstoken}`, { ..._.omit(note, ['id', 'organizationId', 'conversationId', 'owner', 'created', 'modified']), deleted: true });
            setOpenDialog(false);
            onUpdate({ note: { ...note, deleted: true }});
            setDeleting(false);
        } catch (err) {
            console.error('Failed to delete note:', err);
            setDeleting(false);
            setDeletingFailed(true);
            await new Promise(resolve => setTimeout(resolve, 3000));
            setDeletingFailed(false);
        }
    };

    async function onRestore() {
        setRestoring(true);
        try {
            const { data } = await axios.put(`/api/emailconversations/${encodeURIComponent(conversationId)}/note/${note.id}?organizationId=${organization.id}&accesstoken=${accesstoken}`, { ..._.omit(note, ['id', 'organizationId', 'conversationId', 'owner', 'created', 'modified']), deleted: false });
            onUpdate({ note: { ...note, deleted: false }});
        } catch (err) {
            console.error('Failed to restore note:', err);
            setRestoringFailed(true);
            setRestoring(false);
            await new Promise(resolve => setTimeout(resolve, 3000));
            setRestoringFailed(false);
        }
        setRestoring(false);
    }

    if (note.deleted) {
        return (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='caption' component={Box}>Note deleted.</Typography>
                <Button onClick={onRestore} color={restoringFailed ? 'error' : 'primary'} disabled={restoring}>
                    {restoring ? <CircularProgress size={20} /> : 
                     restoringFailed ? <FontAwesomeIcon icon={faXmark} /> :
                     <FontAwesomeIcon icon={faUndo} />}
                </Button>
            </Paper>
        )
    }

    return (
        <>
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Delete Note
                </DialogTitle>
                <DialogContent>
                    {deleting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CircularProgress size={20} />
                            <DialogContentText>
                                Deleting note...
                            </DialogContentText>
                        </Box>
                    ) : deletingFailed ? (
                        <DialogContentText id="alert-dialog-description" sx={{ color: 'error.main' }}>
                            Failed to delete note
                        </DialogContentText>
                    ) : (
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to delete this note?
                        </DialogContentText>
                    )}
                </DialogContent>
                {!deletingFailed && (
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)} disabled={deleting}>Cancel</Button>
                        <Button onClick={handleDelete} color="error" autoFocus disabled={deleting}>
                            Delete
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <Paper
                elevation={6}
                sx={{
                    backgroundColor: "#FFF9C4", // Lighter post-it yellow
                    padding: 2,
                    width: '100%',
                    minHeight: 200,
                    boxShadow: "4px 4px 6px rgba(0,0,0,0.3)",
                    transform: "rotate(-1.5deg)", 
                    borderRadius: "4px",
                    position: "relative",
                    marginBottom: 2,
                    "& *": {
                        fontFamily: "Comic Neue, cursive",
                        fontWeight: 500,
                        fontStyle: "normal",
                    },
                    "&:before": {
                        content: '""',
                        position: "absolute", 
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background:
                            "linear-gradient(to bottom, rgba(255,255,255,0.2), transparent 60%)",
                        pointerEvents: "none",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        top: "-10px", // above the paper
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "16px", 
                        height: "16px",
                        borderRadius: "50%",
                        background: "radial-gradient(circle at 30% 30%, #e74c3c, #c0392b)", // red pushpin look
                        boxShadow:
                            "0 2px 4px rgba(0,0,0,0.4), inset 0 -1px 2px rgba(255,255,255,0.5)",
                        zIndex: 10,
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant='caption' component={Box}>{note.owner} {note.type === 'public' ? '(Public)' : '(Private)'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant='caption' component={Box}>{dayjs(note.created).format(organization.dateformat || 'DD/MM/YYYY HH:mm')}</Typography>
                        <IconButton 
                            size='small'
                            color='error'
                            onClick={() => setOpenDialog(true)}
                            sx={{
                                minWidth: 'unset',
                                padding: '2px 8px',
                                fontSize: '0.75rem'
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ mt: 1, maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                    <EmailIframe html={styledHtml} />
                </Box>
            </Paper>
        </>
    )
}

function stringAvatar(name) {
    if (!name) return { sx: { bgcolor: '#94a3b8', color: '#ffffff' }, children: '?' };
    const [ first, last ] = name?.split(' ');
    return {
        sx: {
            bgcolor: stringToModernColor(name),
            color: '#ffffff'
        },
        children: `${first[0]}${last ? last[0] : first[1] || ''}`.toUpperCase(),
    };
}

function stringToModernColor(string) {
    // Modern desaturated color palette (slate/indigo tones)
    const colors = [
        '#6366f1', // indigo-500
        '#475569', // slate-600
        '#64748b', // slate-500
        '#4f46e5', // indigo-600
        '#334155', // slate-700
        '#7c3aed', // violet-600
        '#5b21b6', // violet-700
    ];
    
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
}

export function EmailIframe({ html }) {
    const iframeRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState('400px');
    const containerRef = useRef(null);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || !html) return;

        let pollIntervalRef = null;
        let resizeObserver = null;

        const updateHeight = () => {
            try {
                // Try to access iframe content for height calculation
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc && iframeDoc.body) {
                    // Calculate height based on content
                    const height = Math.max(
                        iframeDoc.body.scrollHeight,
                        iframeDoc.body.offsetHeight,
                        iframeDoc.documentElement?.scrollHeight || 0,
                        iframeDoc.documentElement?.offsetHeight || 0
                    );
                    setIframeHeight(`${Math.max(height, 100)}px`);
                } else {
                    // Fallback: use iframe's scrollHeight
                    setIframeHeight(`${Math.max(iframe.scrollHeight || 400, 400)}px`);
                }
            } catch (e) {
                // Sandbox restriction - use fallback
                try {
                    setIframeHeight(`${Math.max(iframe.scrollHeight || 400, 400)}px`);
                } catch (e2) {
                    setIframeHeight('400px');
                }
            }
        };

        const handleLoad = () => {
            // Wait for content to render
            setTimeout(updateHeight, 100);
            
            // Also wait for images to load
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc) {
                    const images = iframeDoc.querySelectorAll('img');
                    let imagesLoaded = 0;
                    const totalImages = images.length;

                    if (totalImages === 0) {
                        updateHeight();
                    } else {
                        images.forEach(img => {
                            if (img.complete) {
                                imagesLoaded++;
                            } else {
                                img.addEventListener('load', () => {
                                    imagesLoaded++;
                                    if (imagesLoaded === totalImages) {
                                        updateHeight();
                                    }
                                });
                                img.addEventListener('error', () => {
                                    imagesLoaded++;
                                    if (imagesLoaded === totalImages) {
                                        updateHeight();
                                    }
                                });
                            }
                        });
                        if (imagesLoaded === totalImages) {
                            updateHeight();
                        }
                    }
                }
            } catch (e) {
                // Can't access iframe content - use fallback
                updateHeight();
            }
            
            // Set up polling as fallback to catch dynamic content changes
            pollIntervalRef = setInterval(() => {
                updateHeight();
            }, 500);
            
            // Clear polling after 10 seconds (content should be loaded by then)
            setTimeout(() => {
                if (pollIntervalRef) {
                    clearInterval(pollIntervalRef);
                    pollIntervalRef = null;
                }
            }, 10000);
        };

        iframe.addEventListener('load', handleLoad);
        
        // Use ResizeObserver if we can access the iframe content
        try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (iframeDoc && iframeDoc.body && window.ResizeObserver) {
                resizeObserver = new ResizeObserver(() => {
                    updateHeight();
                });
                resizeObserver.observe(iframeDoc.body);
            }
        } catch (e) {
            // Can't access iframe content for ResizeObserver - polling will handle it
        }

        return () => {
            iframe.removeEventListener('load', handleLoad);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (pollIntervalRef) {
                clearInterval(pollIntervalRef);
            }
        };
    }, [html]);

    // Sanitize HTML for security
    const sanitizedHtml = html; // Emails from APIs are already sanitized

    // Wrap in a complete HTML document with Poppins as default font
    const htmlDocument = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body {
            font-family: 'Poppins', sans-serif;
            margin: 0;
            padding: 16px;
        }
        * {
            font-family: inherit;
        }
    </style>
</head>
<body>
    ${sanitizedHtml}
</body>
</html>`;

    return (
        <div ref={containerRef} style={{ width: '100%' }}>
            <iframe
                ref={iframeRef}
                srcDoc={htmlDocument}
                style={{
                    width: '100%',
                    height: iframeHeight,
                    border: 'none',
                    display: 'block'
                }}
                sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts"
                title="Email content"
            />
        </div>
    );
}